import { publicHttp } from "%/http";
import * as QueryString from "qs";
import { setOAuthToken, getOAuthToken, clearOAuthToken } from "%/token";

export interface KakaoRequest {
  code: string | null;
}

interface KakaoResponse {
  access_token: string;
  token_type: string;
  refresh_token: string;
  expires_in: number;
  refresh_token_expires_in: number;
}

export interface KakaoMeResponse {
  id: string;
  connected_at: Date;
  properties: Properties;
  kakao_account: KakaoAccount;
}

interface KakaoAccount {
  profile_nickname_needs_agreement: string;
  profile_image_needs_agreement: string;
  profile: Profile;
  has_email: string;
  email_needs_agreement: string;
  is_email_valid: string;
  is_email_verified: string;
  email: string;
}

interface Profile {
  nickname: string;
  thumbnail_image_url: string;
  profile_image_url: string;
  is_default_image: string;
  is_default_nickname: string;
}

interface Properties {
  nickname: string;
  profile_image: string;
  thumbnail_image: string;
}

// Google OAuth 인터페이스
export interface GoogleRequest {
  code: string | null;
}

interface GoogleTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token?: string;
  scope: string;
}

export interface GoogleUserResponse {
  id: string;
  email: string;
  verified_email: boolean;
  name: string;
  given_name: string;
  family_name: string;
  picture: string;
  locale: string;
}

// Naver OAuth 인터페이스
export interface NaverRequest {
  code: string | null;
  state?: string | null;
}

interface NaverTokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export interface NaverUserResponse {
  resultcode: string;
  message: string;
  response: {
    id: string;
    nickname: string;
    name: string;
    email: string;
    gender: string;
    age: string;
    birthday: string;
    profile_image: string;
    birthyear: string;
    mobile: string;
  };
}

// ⚠️ 애플은 다른 SNS 와 달리 AUTH_URL 풀 redirect 를 쓰지 않는다.
// response_mode 가 form_post 라 콜백이 POST 로 오는데 정적 프론트 라우트는 POST 를 받을 수 없고,
// 백엔드에도 그 콜백(/api/oauth/apple)을 받는 엔드포인트가 없다(있는 건 /oauth/apple-{customer,business}).
// 웹 애플 로그인은 JS SDK 팝업 방식 — #/auth/appleSignIn 의 signInWithAppleWeb 참조.

export const KAKAO_AUTH_URL = `https://kauth.kakao.com/oauth/authorize?client_id=${
  import.meta.env.VITE_KAKAO_KEY
}&redirect_uri=${import.meta.env.VITE_KAKAO_REDIRECT}&response_type=code`;

export const GOOGLE_AUTH_URL = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${
  import.meta.env.VITE_GOOGLE_KEY
}&redirect_uri=${import.meta.env.VITE_GOOGLE_REDIRECT}&response_type=code&scope=email profile`;

export const NAVER_AUTH_URL = `https://nid.naver.com/oauth2.0/authorize?client_id=${
  import.meta.env.VITE_NAVER_KEY
}&redirect_uri=${import.meta.env.VITE_NAVER_REDIRECT}&response_type=code&state=${Date.now()}`;

export const signInForKakao = async ({ code }: KakaoRequest) => {
  const { data } = await publicHttp.post<KakaoResponse>(
    "https://kauth.kakao.com/oauth/token",
    QueryString.stringify({
      grant_type: "authorization_code",
      client_id: import.meta.env.VITE_KAKAO_KEY,
      redirect_uri: import.meta.env.VITE_KAKAO_REDIRECT,
      client_secret: import.meta.env.VITE_KAKAO_SECRET,
      code,
    }),
    {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded;charset=utf-8",
      },
    }
  );

  // 카카오 액세스 토큰 저장
  setOAuthToken("kakao", data.access_token);

  return publicHttp.get<KakaoMeResponse>("https://kapi.kakao.com/v2/user/me", {
    headers: {
      Authorization: ` Bearer ${data.access_token}`,
      "Content-Type": "application/x-www-form-urlencoded;charset=utf-8",
    },
  });
};

// 카카오 로그아웃
export const signOutForKakao = async (): Promise<void> => {
  const kakaoAccessToken = getOAuthToken("kakao");

  if (kakaoAccessToken) {
    try {
      await publicHttp.post(
        "https://kapi.kakao.com/v1/user/logout",
        {},
        {
          headers: {
            Authorization: `Bearer ${kakaoAccessToken}`,
            "Content-Type": "application/x-www-form-urlencoded;charset=utf-8",
          },
        }
      );
      console.log("카카오 로그아웃 성공");
    } catch {
      console.error("카카오 로그아웃 실패");
    } finally {
      clearOAuthToken("kakao");
    }
  }
};

export const signInForGoogle = async ({ code }: GoogleRequest) => {
  console.log("[signInForGoogle] 토큰 교환 요청 시작", {
    endpoint: "https://oauth2.googleapis.com/token",
    redirect_uri: import.meta.env.VITE_GOOGLE_REDIRECT,
    hasCode: !!code,
  });
  try {
    const { data } = await publicHttp.post<GoogleTokenResponse>(
      "https://oauth2.googleapis.com/token",
      QueryString.stringify({
        grant_type: "authorization_code",
        client_id: import.meta.env.VITE_GOOGLE_KEY,
        client_secret: import.meta.env.VITE_GOOGLE_SECRET,
        redirect_uri: import.meta.env.VITE_GOOGLE_REDIRECT,
        code,
      }),
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      }
    );
    console.log("[signInForGoogle] 토큰 교환 성공", { access_token_length: data.access_token?.length });
    setOAuthToken("google", data.access_token);

    console.log("[signInForGoogle] 사용자 정보 요청");
    const result = await publicHttp.get<GoogleUserResponse>(
      "https://www.googleapis.com/oauth2/v2/userinfo",
      {
        headers: {
          Authorization: `Bearer ${data.access_token}`,
        },
      }
    );
    console.log("[signInForGoogle] 사용자 정보 응답", { status: result.status, hasData: !!result.data });
    return result;
  } catch (error: any) {
    console.error("[signInForGoogle] 실패 ❌", {
      message: error?.message,
      status: error?.response?.status,
      data: error?.response?.data,
      isCORS: error?.message?.includes("Network") || error?.message?.includes("CORS"),
      hint: "Google OAuth 콘솔 redirect URI 등록 확인 + CORS 가능성",
    });
    throw error;
  }
};

// 구글 로그아웃 — 토큰 revoke 호출 (구글은 별도 logout 엔드포인트 없음)
export const signOutForGoogle = async (): Promise<void> => {
  const accessToken = getOAuthToken("google");
  try {
    if (accessToken) {
      await publicHttp.post(
        `https://oauth2.googleapis.com/revoke?token=${accessToken}`,
        null,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );
      console.log("[signOutForGoogle] revoke 성공");
    }
  } catch (e) {
    console.error("[signOutForGoogle] revoke 실패", e);
  } finally {
    clearOAuthToken("google");
  }
};

export const signInForNaver = async ({ code, state }: NaverRequest) => {
  const { data } = await publicHttp.post<NaverTokenResponse>(
    "https://nid.naver.com/oauth2.0/token",
    QueryString.stringify({
      grant_type: "authorization_code",
      client_id: import.meta.env.VITE_NAVER_KEY,
      client_secret: import.meta.env.VITE_NAVER_SECRET,
      redirect_uri: import.meta.env.VITE_NAVER_REDIRECT,
      code,
      state,
    }),
    {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    }
  );

  // 네이버 액세스 토큰 저장
  setOAuthToken("naver", data.access_token);

  return publicHttp.get<NaverUserResponse>(
    "https://openapi.naver.com/v1/nid/me",
    {
      headers: {
        Authorization: `Bearer ${data.access_token}`,
      },
    }
  );
};

// 네이버 로그아웃 — 별도 logout 엔드포인트 없음, 토큰만 삭제
// (네이버는 회원탈퇴 시 unlink 로 access_token 삭제 가능 — unlinkForNaver 참조)
export const signOutForNaver = async (): Promise<void> => {
  console.log("[signOutForNaver] 토큰만 삭제 (네이버는 logout API 없음)");
  clearOAuthToken("naver");
};

// 애플 로그아웃 (애플은 별도 로그아웃 API가 없으므로 토큰만 삭제)
export const signOutForApple = async (): Promise<void> => {
  try {
    // 애플은 별도 로그아웃 API가 없으므로 토큰만 삭제
    console.log("애플 로그아웃 처리");
  } catch {
    console.error("애플 로그아웃 실패");
  } finally {
    clearOAuthToken("apple");
  }
};

// 통합 OAuth 로그아웃 함수
export const signOutForOAuth = async (): Promise<void> => {
  console.log("=== OAuth 로그아웃 시작 ===");

  // SNS 타입 감지 (여러 방법 시도)
  let snsType = sessionStorage.getItem("local-sign-sns-type");
  console.log("sessionStorage에서 가져온 값:", snsType);

  if (!snsType) {
    // sessionStorage에 없으면 localStorage에서 시도
    snsType = localStorage.getItem("local-sign-sns-type");
    console.log("localStorage에서 가져온 값:", snsType);
  }

  if (!snsType) {
    // URL 파라미터에서도 확인
    const urlParams = new URLSearchParams(window.location.search);
    const urlSnsType = urlParams.get("sns-type");
    if (urlSnsType) {
      snsType = urlSnsType;
      console.log("URL 파라미터에서 가져온 값:", snsType);
    }
  }

  if (!snsType) {
    console.log("SNS 타입을 찾을 수 없어서 OAuth 로그아웃을 건너뜁니다.");
    return;
  }

  // useStorage 훅이 JSON.stringify로 저장하므로 파싱 시도
  let parsedSnsType: string;
  try {
    parsedSnsType = JSON.parse(snsType);
    console.log("JSON 파싱 성공:", parsedSnsType);
  } catch {
    // JSON 파싱 실패 시 원본 값 사용
    parsedSnsType = snsType;
    console.log("JSON 파싱 실패, 원본 값 사용:", parsedSnsType);
  }

  try {
    console.log("최종 SNS 타입:", parsedSnsType);

    switch (parsedSnsType) {
      case "SNS_TYPE_KAKAO":
        console.log("카카오 로그아웃 시작...");
        await signOutForKakao();
        console.log("카카오 로그아웃 완료");
        break;
      case "SNS_TYPE_GOOGLE":
        console.log("구글 로그아웃 시작...");
        await signOutForGoogle();
        console.log("구글 로그아웃 완료");
        break;
      case "SNS_TYPE_NAVER":
        console.log("네이버 로그아웃 시작...");
        await signOutForNaver();
        console.log("네이버 로그아웃 완료");
        break;
      case "SNS_TYPE_APPLE":
        console.log("애플 로그아웃 시작...");
        await signOutForApple();
        console.log("애플 로그아웃 완료");
        break;
      default:
        console.log(`알 수 없는 SNS 타입: ${parsedSnsType}`);
        break;
    }
  } catch {
    console.error("OAuth 로그아웃 중 오류 발생");
  }

  console.log("=== OAuth 로그아웃 종료 ===");
};

/* ════════════════════════════════════════════════════════════
 *  강제 재인증 (force re-login) — 로그아웃 후 다음 로그인 시 SSO 자동로그인 방지
 *  - 로그아웃하면 setForceRelogin() 호출해 플래그 세팅
 *  - 다음 OAuth URL 호출 시 buildAuthUrlWithPrompt() 가 prompt 파라미터 추가
 *  - 사용 후 플래그 자동 제거
 * ════════════════════════════════════════════════════════════ */

const FORCE_RELOGIN_FLAG = "mung-force-relogin";

export const setForceRelogin = () => {
  try {
    localStorage.setItem(FORCE_RELOGIN_FLAG, "1");
  } catch {
    // ignore
  }
};

/**
 * 강제 재인증 플래그 제거 — 로그인 성공 시점에 호출.
 * (사용자가 팝업을 닫거나 취소했을 때는 제거하면 안 됨 — 다시 시도 시에도 강제 재인증 필요)
 */
export const clearForceRelogin = () => {
  try {
    localStorage.removeItem(FORCE_RELOGIN_FLAG);
  } catch {
    // ignore
  }
};

export const buildAuthUrlWithPrompt = (
  baseUrl: string,
  provider: "kakao" | "google" | "naver" | "apple"
): string => {
  const force = localStorage.getItem(FORCE_RELOGIN_FLAG) === "1";
  if (!force) return baseUrl;
  // ⚠️ 여기서는 플래그를 제거하지 않음.
  // 사용자가 팝업을 닫거나 취소해도 다음 시도 때 다시 prompt=login 이 적용되어야 함.
  // 실제 로그인 성공 시 clearForceRelogin() 으로 명시적으로 제거.

  const sep = baseUrl.includes("?") ? "&" : "?";
  switch (provider) {
    case "kakao":
    case "google":
    case "apple":
      // OAuth 표준: 로그인 화면 강제 표시
      return baseUrl + sep + "prompt=login";
    case "naver":
      // 네이버 전용: 동일 효과
      return baseUrl + sep + "auth_type=reprompt";
  }
};

/* ════════════════════════════════════════════════════════════
 *  회원탈퇴용 — SNS 연결 끊기 (unlink/revoke)
 *  단순 로그아웃과 달리 사용자가 우리 서비스에 부여한 OAuth 권한을
 *  완전히 해제. 다음 로그인 시 동의 화면부터 다시 시작됨.
 * ════════════════════════════════════════════════════════════ */

export const unlinkForKakao = async (): Promise<void> => {
  const accessToken = getOAuthToken("kakao");
  try {
    if (accessToken) {
      await publicHttp.post(
        "https://kapi.kakao.com/v1/user/unlink",
        {},
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/x-www-form-urlencoded;charset=utf-8",
          },
        }
      );
      console.log("[unlinkForKakao] 연결 끊기 성공");
    }
  } catch (e) {
    console.error("[unlinkForKakao] 연결 끊기 실패", e);
  } finally {
    clearOAuthToken("kakao");
  }
};

export const unlinkForGoogle = async (): Promise<void> => {
  // 구글은 로그아웃 = revoke 와 동일 (권한 자체 해제)
  await signOutForGoogle();
};

export const unlinkForNaver = async (): Promise<void> => {
  const accessToken = getOAuthToken("naver");
  try {
    if (accessToken) {
      // 네이버는 grant_type=delete 로 토큰 무효화
      // ⚠️ CORS 차단될 수 있음 — 백엔드 프록시 권장
      await publicHttp.post(
        "https://nid.naver.com/oauth2.0/token",
        QueryString.stringify({
          grant_type: "delete",
          client_id: import.meta.env.VITE_NAVER_KEY,
          client_secret: import.meta.env.VITE_NAVER_SECRET,
          access_token: accessToken,
          service_provider: "NAVER",
        }),
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );
      console.log("[unlinkForNaver] 연결 끊기 성공");
    }
  } catch (e) {
    console.error("[unlinkForNaver] 연결 끊기 실패 (CORS 가능성)", e);
  } finally {
    clearOAuthToken("naver");
  }
};

export const unlinkForApple = async (): Promise<void> => {
  // ⚠️ Apple revoke 는 client_secret JWT 가 필요해서 반드시 백엔드에서 처리해야 함.
  // 백엔드 회원탈퇴 API 가 내부적으로 호출하는 것이 정석.
  // 프론트는 토큰만 정리.
  console.log("[unlinkForApple] 토큰 삭제만 (revoke 는 백엔드에서 처리)");
  clearOAuthToken("apple");
};

/**
 * 통합 SNS 연결 끊기 — 회원탈퇴 시 호출.
 * sessionStorage/localStorage 의 SNS 타입을 자동 감지.
 */
export const unlinkForOAuth = async (): Promise<void> => {
  console.log("=== SNS 연결 끊기 시작 ===");

  // SNS 타입 감지 (signOutForOAuth 와 동일 로직)
  let snsType =
    sessionStorage.getItem("local-sign-sns-type") ||
    localStorage.getItem("local-sign-sns-type");

  if (!snsType) {
    console.log("SNS 타입 없음 — 연결 끊기 건너뜀");
    return;
  }

  let parsedSnsType: string;
  try {
    parsedSnsType = JSON.parse(snsType);
  } catch {
    parsedSnsType = snsType;
  }

  try {
    switch (parsedSnsType) {
      case "SNS_TYPE_KAKAO":
        await unlinkForKakao();
        break;
      case "SNS_TYPE_GOOGLE":
        await unlinkForGoogle();
        break;
      case "SNS_TYPE_NAVER":
        await unlinkForNaver();
        break;
      case "SNS_TYPE_APPLE":
        await unlinkForApple();
        break;
      default:
        console.log(`알 수 없는 SNS 타입: ${parsedSnsType}`);
    }
  } catch (e) {
    console.error("SNS 연결 끊기 중 오류", e);
  }

  console.log("=== SNS 연결 끊기 종료 ===");
};

// export const GOOGLE_CLIENT_ID = '610382326788-qum4ctpkjbig3c6773ia5nkcejvspctb.apps.googleusercontent.com'
// export const GOOGLE_PASSWORD = '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'
// export const GOOGLE_AUTH_URL = `//accounts.google.com/o/oauth2/v2/auth?client_id=${import.meta.env.VITE_GOOGLE_KEY}&redrect_uri=${}&response_type=code&scope=email profile`
// export const signInForGoogle = () =>
//     xior.post<unknown>(
//         "https://kauth.kakao.com/oauth/token",
//         QueryString.stringify({
//             grant_type: "authorization_code",
//             client_id: REST_API_KEY,
//             redirect_uri: REDIRECT_URI,
//             client_secret: CLIENT_SECRET,
//             code
//         }),
//         {
//             headers: {
//                 "Content-Type": "application/x-www-form-urlencoded;charset=utf-8",
//             },
//         }
//     )
